import React, { useEffect, useMemo, useState } from "react";
import {
  GameConfigs,
  GameTypes,
  isPerformanceGame,
  type Config
} from "kadow-jetpack-model";
import { tt } from "../../../../utils/i18n/i18n";
import GamesSelectorNoFormikWrapper from "./GamesSelectorNoFormikWrapper";
import { useDispatch } from "react-redux";
import EventsSelectorContent from "../module/EventSelectorContent";
import {
  updateJetpackConfigThunk,
  useJetpackConfigSelector
} from "../../../../redux/jetpackConfig";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import { useEventsSelector } from "../../../../redux/actions/wheel";
import { GameThumbnailDTO } from "../../../../model/GameDTO";

import { buildGamesList } from "../../../../utils/helpers/buildGamesList";
import { getDisplayNameForGameType } from "../../../../utils/helpers/getDisplayNameForGameType";
import { ActiveEvent } from "kadow-jetpack-model";
import { NEUTRAL_EVENT_ID } from "../../../../utils/constants";

enum EventStep {
  GameType = 0,
  Event = 1
}

interface Props {
  includeGamesOfChance: boolean;
  includePerformanceGames: boolean;
  handleSubmit: (values: Config) => void;
  hideModal: () => void;
}

export default function EventsSelector(props: Props) {
  const dispatch = useDispatch();
  const jetpackConfig = useJetpackConfigSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const [step, setStep] = useState(EventStep.GameType);
  const [selectedGame, setSelectedGame] = useState<GameThumbnailDTO | null>(
    null
  );

  const events = useEventsSelector();

  const hasSkins = useMemo(() => {
    return !!accountToDisplay?.saasFeatures?.s;
  }, [accountToDisplay]);

  const goToStep = (step: EventStep) => {
    setStep(step);
  };

  const handleSubmitEventStep = (
    newEvent: ActiveEvent | null,
    isDefaultSkinSelected: boolean
  ) => {
    if (
      !jetpackConfig ||
      !accountToDisplay?.trackingID ||
      !accountToDisplay?.token ||
      !selectedGame
    ) {
      return;
    }

    const activeEvents = [...(jetpackConfig?.activeEvents || [])];

    if (isDefaultSkinSelected) {
      const isPerfGame = isPerformanceGame(selectedGame.name as GameTypes);
      if (isPerfGame) {
        const activeEvent: ActiveEvent = {
          id: NEUTRAL_EVENT_ID,
          gameType: selectedGame.name,
          ets: -1,
          sts: -1
        };
        activeEvents.push(activeEvent);
        dispatch(
          updateJetpackConfigThunk(
            {
              activeEvents
            },
            accountToDisplay.trackingID,
            accountToDisplay?.token
          )
        );
      } else {
        const name = selectedGame.name as keyof GameConfigs;
        const newGame = {
          name,
          config: {
            winRate: 1,
            coinsToPlay: 1
          }
        };
        const games = [...(jetpackConfig.games || []), newGame];
        dispatch(
          updateJetpackConfigThunk(
            {
              games
            },
            accountToDisplay.trackingID,
            accountToDisplay?.token
          )
        );
      }
    }

    if (newEvent) {
      activeEvents.push(newEvent);
      dispatch(
        updateJetpackConfigThunk(
          {
            activeEvents
          },
          accountToDisplay.trackingID,
          accountToDisplay?.token
        )
      );
    }
    props.hideModal();
  };

  const handleSubmitGameStep = (values: GameThumbnailDTO[]) => {
    if (values && values[0]) {
      setSelectedGame(values[0]);
    }
  };

  useEffect(() => {
    if (!selectedGame) {
      return;
    }

    if (hasSkins) {
      setStep(EventStep.Event);
    } else {
      handleSubmitEventStep(null, true);
    }
  }, [selectedGame]);

  const GAMES_LIST = useMemo(() => {
    const games = buildGamesList(
      props.includeGamesOfChance,
      props.includePerformanceGames,
      false,
      jetpackConfig,
      true,
      events,
      false
    );

    const gamesDedupedByName = games.filter(
      (game, index) =>
        games.findIndex((other) => other.name === game.name) === index
    );

    return gamesDedupedByName.sort((firstGame, secondGame) =>
      (getDisplayNameForGameType(firstGame.name) ?? "").localeCompare(
        getDisplayNameForGameType(secondGame.name) ?? ""
      )
    );
  }, [step, jetpackConfig]);

  const CONTENT = useMemo(() => {
    switch (step) {
      case EventStep.GameType:
        return (
          <GamesSelectorNoFormikWrapper
            handleSubmit={handleSubmitGameStep}
            handleGoBack={() => {}}
            gamesList={GAMES_LIST}
            useSubmit={false}
            jetpackConfig={jetpackConfig}
            hasSkins={hasSkins}
          />
        );

      case EventStep.Event:
        return (
          <EventsSelectorContent
            title={tt("select_highlight")}
            description={undefined}
            events={events}
            isGamesSelectable={true}
            onEventSelect={handleSubmitEventStep}
            handleGoBack={() => goToStep(EventStep.GameType)}
            submitBtnTitle={tt("finish")}
            selectedGame={selectedGame}
            selectedEventId={-1}
            canUsePerformanceGames={props.includePerformanceGames}
          />
        );
    }
  }, [step, jetpackConfig]);

  if (jetpackConfig == null) {
    return <>{tt("error")}</>;
  }

  return <div className="mt-2 d-flex flex-column gap-sm">{CONTENT}</div>;
}
